import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  Eye,
  FileSpreadsheet,
  List,
  Package,
  Users,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface SummaryData {
  client: {
    id: number;
    name: string;
    source: string;
    file: string | null;
  };
  completedAt: string | null;
  durationSec: number | null;
  created: {
    employees: number;
    dependents: number;
    plans: number;
    carriers: number;
    enrollments: number;
    enrolled: number;
    waived: number;
    rows: number;
  };
}

interface HubData {
  totals: { total: number; finished: number; inProgress: number; errors: number; notStarted: number };
}

function formatDuration(sec: number | null): string {
  if (sec == null) return "—";
  if (sec < 60) return `${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  const rem = min % 60;
  return rem === 0 ? `${hr}h` : `${hr}h ${rem}m`;
}

export function DoneStep({ jobId }: { jobId: number }) {
  const [, navigate] = useLocation();

  const summary = useQuery<SummaryData>({
    queryKey: [`/api/migration/jobs/${jobId}/summary`],
  });

  const hub = useQuery<HubData>({
    queryKey: ["/api/migration/hub"],
  });

  if (summary.isLoading || !summary.data) {
    return (
      <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
        Loading summary…
      </div>
    );
  }
  const s = summary.data;
  const completedRel = s.completedAt
    ? formatDistanceToNow(new Date(s.completedAt), { addSuffix: true })
    : "—";
  const remainingNotStarted = hub.data?.totals.notStarted ?? 0;

  return (
    <div className="space-y-4">
      {/* Block 1 — Hero */}
      <div
        className="flex items-center gap-4 rounded-xl border p-6"
        style={{
          background:
            "linear-gradient(180deg, #ECFDF5 0%, #FAFAF9 100%)",
          borderColor: "#A7F3D0",
        }}
      >
        <div
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white"
          style={{ borderColor: "#A7F3D0", borderWidth: 1, color: "#059669" }}
        >
          <CheckCircle2 className="h-7 w-7" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xl font-semibold tracking-tight">
            {s.client.name} migrated
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            From{" "}
            <span className="font-medium capitalize text-foreground">
              {s.client.source}
            </span>
            {s.client.file && (
              <>
                {" · "}
                <span className="font-mono">{s.client.file}</span>
              </>
            )}
            {" · "}confirmed {completedRel}
            {s.durationSec != null && (
              <>
                {" · "}
                {formatDuration(s.durationSec)}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Block 2 — What was created */}
      <Card>
        <CardContent className="p-0">
          <div className="border-b px-4 py-3">
            <div className="text-sm font-semibold">What was created</div>
            <div className="text-xs text-muted-foreground">
              Live records now in Administr.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2.5 p-4 md:grid-cols-4">
            <StatCell
              label="Employees"
              value={s.created.employees}
              sub={`+ ${s.created.dependents} dependent${s.created.dependents === 1 ? "" : "s"}`}
              icon={<Users className="h-4 w-4" />}
              tileClass="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
              valueClass="text-blue-700 dark:text-blue-300"
            />
            <StatCell
              label="Plans"
              value={s.created.plans}
              sub={`${s.created.carriers} carrier${s.created.carriers === 1 ? "" : "s"}`}
              icon={<Package className="h-4 w-4" />}
              tileClass="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
              valueClass="text-purple-700 dark:text-purple-300"
            />
            <StatCell
              label="Enrollments"
              value={s.created.enrollments}
              sub={`${s.created.enrolled} enrolled · ${s.created.waived} waived`}
              icon={<ClipboardCheck className="h-4 w-4" />}
              tileClass="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
              valueClass="text-emerald-700 dark:text-emerald-300"
            />
            <StatCell
              label="Rows committed"
              value={s.created.rows}
              sub="From source CSV"
              icon={<FileSpreadsheet className="h-4 w-4" />}
              tileClass="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
              valueClass="text-amber-700 dark:text-amber-300"
            />
          </div>
        </CardContent>
      </Card>

      {/* Block 4 — What's next */}
      <Card>
        <CardContent className="p-5">
          <div className="text-sm font-semibold">What's next?</div>
          <div className="mb-4 text-xs text-muted-foreground">
            Verify the data, head back to the queue, or jump straight into the
            next client.
          </div>
          <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
            <NextCard
              tone="primary"
              icon={<Eye className="h-4 w-4" />}
              title="Open this client"
              body={`Verify ${s.created.employees} employees, ${s.created.plans} plans, and ${s.created.enrollments} enrollments are correct.`}
              cta="Open client"
              onClick={() => navigate(`/clients/${s.client.id}?tab=benefits`)}
            />
            <NextCard
              tone="secondary"
              icon={<List className="h-4 w-4" />}
              title="Back to Migration Hub"
              body={
                remainingNotStarted > 0
                  ? `${remainingNotStarted} client${remainingNotStarted === 1 ? "" : "s"} still need their census imported.`
                  : "All clients are migrated."
              }
              cta="Go to Hub"
              onClick={() => navigate("/migration/hub")}
            />
          </div>
          <div className="mt-3 text-center text-[11.5px] text-muted-foreground">
            Need to fix something? You can edit this client's data from their
            profile.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCell({
  label,
  value,
  sub,
  icon,
  tileClass,
  valueClass,
}: {
  label: string;
  value: number;
  sub: string;
  icon: React.ReactNode;
  tileClass: string;
  valueClass: string;
}) {
  return (
    <div className="rounded-md border bg-card px-3.5 py-3">
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex h-6 w-6 items-center justify-center rounded-md ${tileClass}`}
        >
          {icon}
        </span>
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
      </div>
      <div
        className={`mt-1.5 font-mono text-2xl font-semibold tracking-tight ${valueClass}`}
      >
        {value}
      </div>
      <div className="mt-0.5 text-[11.5px] text-muted-foreground">{sub}</div>
    </div>
  );
}

function NextCard({
  tone,
  icon,
  title,
  body,
  cta,
  onClick,
  disabled,
}: {
  tone: "primary" | "secondary" | "ghost";
  icon: React.ReactNode;
  title: string;
  body: string;
  cta: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  const styles =
    tone === "primary"
      ? {
          container:
            "border-2 border-foreground bg-card",
          tile: "bg-foreground text-background",
        }
      : tone === "secondary"
        ? {
            container: "border bg-card",
            tile: "bg-muted text-foreground",
          }
        : {
            container: "border bg-muted/40",
            tile: "bg-card text-muted-foreground",
          };

  return (
    <div
      className={`flex flex-col gap-2.5 rounded-lg p-3.5 ${styles.container}`}
    >
      <div
        className={`flex h-8 w-8 items-center justify-center rounded-lg ${styles.tile}`}
      >
        {icon}
      </div>
      <div>
        <div className="text-[13.5px] font-semibold">{title}</div>
        <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
          {body}
        </div>
      </div>
      <Button
        variant={tone === "primary" ? "default" : "outline"}
        size="sm"
        onClick={onClick}
        disabled={disabled}
        className="self-start"
      >
        {cta}
        <ArrowRight className="ml-1 h-3 w-3" />
      </Button>
    </div>
  );
}
